import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { needsCookieNotice, acknowledgeCookieNotice } from "@/lib/cookieNotice";

/**
 * One-time browser storage notice. The app only uses strictly necessary storage
 * and storage for features the user requests, so no consent choice is needed
 * (PECR) — this is a transparent notice with a link to the Cookie Policy.
 */
export default function CookieNotice() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Small delay so it doesn't compete with the first paint
    const id = setTimeout(() => setOpen(needsCookieNotice()), 800);
    return () => clearTimeout(id);
  }, []);

  if (!open) return null;

  const close = () => {
    acknowledgeCookieNotice();
    setOpen(false);
  };

  return (
    <div
      role="region"
      aria-label={t("legal.noticeTitle")}
      data-testid="cookie-notice"
      className="fixed inset-x-3 bottom-[calc(5.25rem+env(safe-area-inset-bottom))] z-[70] animate-fade-up sm:inset-x-auto sm:bottom-6 sm:left-6 sm:max-w-sm lg:bottom-6"
    >
      <div className="rounded-2xl border border-border bg-popover/95 p-4 text-popover-foreground shadow-2xl shadow-black/40 backdrop-blur-xl">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
            <Cookie className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold">{t("legal.noticeTitle")}</p>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{t("legal.noticeBody")}</p>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-end gap-2">
          <Link
            to="/cookies"
            onClick={close}
            className="focus-ring inline-flex h-9 items-center rounded-lg px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            {t("legal.cookiePolicy")}
          </Link>
          <button
            type="button"
            onClick={close}
            className="focus-ring inline-flex h-9 items-center rounded-lg bg-primary px-4 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            data-testid="cookie-notice-ok"
          >
            {t("legal.noticeOk")}
          </button>
        </div>
      </div>
    </div>
  );
}
